/**
 * Default Help Center content
 * This file contains all built-in help articles for TraceKit
 */

import { HelpArticleData, HelpCategory } from './types';

export const defaultHelpContent: HelpArticleData[] = [
  // ============================================
  // GETTING STARTED
  // ============================================
  {
    id: 'gs-001',
    title: 'Welcome to TraceOS',
    category: 'getting-started',
    summary: 'An introduction to the TraceOS evidence-driven platform',
    content: `
# Welcome to TraceOS

TraceOS is an evidence-driven ETL + RAG platform built on four core principles:

1. **If it isn't cited, it isn't said** - Every answer requires evidence
2. **Every step is audited** - Complete lineage tracking
3. **Policy enforcement by default** - SOP/EGBC guardrails built-in
4. **No hallucinations tolerated** - Returns "needs evidence" instead

## Key Features

### 📚 Knowledge Base
Upload and manage your documents. TraceOS automatically extracts text, tables, and figures, making everything searchable.

### 🔍 AI-Powered Search
Ask questions in natural language. Get evidence-backed answers with citations to the exact source documents.

### 📊 Entity Management
Query structured data like lab results, measurements, and compliance records using simple filters.

### 📝 Report Generation
Create professional reports with AI assistance, complete with proper citations and evidence trails.

### 🔗 Data Source Integration
Connect external databases to automatically sync and index data.

## Getting Started

1. **Select a Tenant** - Choose your organization from the dropdown
2. **Select a Project** - Choose or create a project to work in
3. **Upload Documents** - Add your documents to the Knowledge Base
4. **Start Asking Questions** - Use the Search or Chat features

## Need Help?

- Click the **?** icon anytime to access this Help Center
- Search for specific topics using the search bar
- Contact support if you need additional assistance
    `,
    keywords: ['introduction', 'welcome', 'overview', 'getting started', 'basics'],
    lastUpdated: '2025-12-01',
  },
  {
    id: 'gs-002',
    title: 'Navigating the Dashboard',
    category: 'getting-started',
    summary: 'Learn how to navigate the TraceOS dashboard',
    content: `
# Navigating the Dashboard

## Main Navigation

The sidebar on the left contains all main features:

- **Home** - Dashboard overview and quick actions
- **Knowledge Base** - Document management
- **Entities** - Structured data explorer
- **Reports** - Report creation and management
- **Search** - AI-powered evidence search
- **Jobs** - Background processing status
- **Sources** - Database connections

## Header Bar

The header bar at the top includes:

- **Sidebar Toggle** - Collapse/expand the navigation
- **Tenant Selector** - Switch between organizations
- **Project Selector** - Switch between projects
- **Help Button (?)** - Access this Help Center
- **User Menu** - Profile and logout options

## Workspace Context

Everything in TraceOS is organized by:

1. **Tenant** - Your organization or client
2. **Project** - A specific project or site within the tenant

Always ensure you have the correct tenant and project selected before uploading documents or querying data.

## Quick Tips

- Use **Ctrl/Cmd + K** to open quick search
- Double-click the sidebar to collapse it
- Hover over icons for tooltips
    `,
    keywords: ['navigation', 'dashboard', 'sidebar', 'menu', 'interface'],
    relatedArticles: ['gs-001', 'gs-003'],
    lastUpdated: '2025-12-01',
  },
  {
    id: 'gs-003',
    title: 'Your First Query',
    category: 'getting-started',
    summary: 'Run your first evidence-based query',
    content: `
# Your First Query

## Step 1: Ensure Documents are Uploaded

Before querying, you need documents in your project:

1. Go to **Knowledge Base** (📁 icon)
2. Click **Upload Documents**
3. Select your files (PDF, DOCX, Excel, etc.)
4. Wait for processing to complete

## Step 2: Open Search or Chat

Navigate to either:
- **Search** - For quick evidence lookups
- **Chat** - For conversational Q&A

## Step 3: Ask Your Question

Enter a natural language question, for example:

> "What are the arsenic concentrations at well MW-03?"

## Step 4: Review the Results

TraceOS will return:

- **Answer** - AI-generated response based on evidence
- **Citations** - Numbered references to source documents
- **Confidence Score** - How confident the system is
- **Evidence Trail** - Click citations to see the exact source

## Understanding Citations

Every statement in the answer includes a citation like [1], [2]. Click these to:

- View the source document
- See the exact page and location
- Verify the evidence yourself

## Tips for Better Queries

✅ **Be Specific**
- Instead of: "Tell me about contamination"
- Try: "What PFAS compounds were detected above 0.07 µg/L?"

✅ **Include Context**
- Mention specific locations, dates, or parameters
- Reference table names or figure numbers if known

✅ **Use Entity Filters**
- For structured data, use the Entity Explorer
- Apply filters for precise results
    `,
    keywords: ['query', 'search', 'question', 'first', 'rag', 'chat'],
    relatedArticles: ['gs-001', 'rag-001'],
    lastUpdated: '2025-12-01',
  },

  // ============================================
  // KNOWLEDGE BASE
  // ============================================
  {
    id: 'kb-001',
    title: 'Uploading Documents',
    category: 'knowledge-base',
    summary: 'How to upload and manage documents',
    content: `
# Uploading Documents

## Supported File Types

TraceOS supports many document types:

| Type | Extensions | Notes |
|------|------------|-------|
| PDF | .pdf | Including scanned PDFs with OCR |
| Word | .docx, .doc | Full formatting preserved |
| Excel | .xlsx, .xls, .csv | Tables extracted as entities |
| PowerPoint | .pptx | Slides and notes extracted |
| Images | .png, .jpg | OCR for text extraction |
| CAD | .dwg, .dxf | Drawing metadata extracted |
| GIS | .gdb | Geodatabase support |

## Upload Methods

### Single File Upload
1. Click **Upload Documents**
2. Select a file
3. Wait for processing

### Bulk Upload
1. Click **Bulk Upload**
2. Drag and drop multiple files
3. Monitor progress in the Jobs panel

### From Data Source
1. Configure a database source
2. Use the sync feature to import

## Processing Pipeline

When you upload a document:

1. **Parsing** - Text and tables extracted
2. **Chunking** - Content split into searchable pieces
3. **Embedding** - AI creates semantic vectors
4. **Indexing** - Added to search indices
5. **Entity Extraction** - Structured data identified

## Monitoring Upload Status

Check the **Jobs** panel to see:
- Processing status
- Error messages (if any)
- Number of chunks created

## Best Practices

- Upload original files when possible (not screenshots)
- Use clear filenames that describe the content
- Group related documents in the same project
- Check job status to ensure processing completes
    `,
    keywords: ['upload', 'documents', 'files', 'import', 'pdf', 'excel'],
    relatedArticles: ['kb-002', 'jobs-001'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },
  {
    id: 'kb-002',
    title: 'Managing Artifacts',
    category: 'knowledge-base',
    summary: 'View, download, and delete artifacts',
    content: `
# Managing Artifacts

## Viewing Artifacts

The Knowledge Base shows all uploaded documents (artifacts):

- **Name** - Original filename
- **Type** - File type (PDF, DOCX, etc.)
- **Size** - File size
- **Uploaded** - Upload date
- **Status** - Processing status

## Artifact Actions

Click on an artifact to access:

### View Details
See metadata including:
- SHA256 hash (for integrity)
- Number of chunks created
- Processing status

### Download
Download the original file

### View Chunks
See how the document was split for search

### Re-ingest
Reprocess the document (useful after system updates)

### Delete
Remove the artifact and all associated data

## Searching Artifacts

Use the search box to filter by:
- Filename
- File type
- Upload date

## Artifact Status

| Status | Meaning |
|--------|---------|
| ✅ Processed | Ready for search |
| 🔄 Processing | Currently being processed |
| ❌ Failed | Processing error (check jobs) |
| ⏳ Queued | Waiting to be processed |

## Duplicate Detection

TraceOS uses SHA256 hashing to detect duplicate files. If you upload the same file twice, it will be recognized automatically.
    `,
    keywords: ['artifacts', 'documents', 'manage', 'delete', 'view'],
    relatedArticles: ['kb-001', 'jobs-001'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },

  // ============================================
  // ENTITIES
  // ============================================
  {
    id: 'ent-001',
    title: 'Understanding Entities',
    category: 'entities',
    summary: 'What are entities and how to use them',
    content: `
# Understanding Entities

## What are Entities?

Entities are structured data extracted from your documents:

- **Sample Results** - Lab analysis results
- **Well Logs** - Borehole/well information
- **Compliance Records** - Regulatory submissions
- **Measurements** - Field readings and observations

## Why Use Entities?

Unlike free-text search, entities allow:

- **Precise Filtering** - Filter by exact values
- **Comparisons** - Find values above/below thresholds
- **Aggregations** - Sum, average, count data
- **Export** - Download as CSV/Excel

## Entity Structure

Each entity has:
- **Type** - What kind of entity (e.g., "sample_result")
- **Fields** - Data columns (e.g., "analyte", "concentration")
- **Source** - Where it came from (document or database)

## Accessing Entities

1. Go to **Entities** in the sidebar
2. Select an entity type
3. Apply filters as needed
4. View or export results

## Common Entity Types

| Type | Description |
|------|-------------|
| sample_result | Lab analysis results |
| borehole_log | Drilling/well logs |
| well_construction | Well installation details |
| compliance_record | Regulatory records |
| measurement | Field measurements |
    `,
    keywords: ['entities', 'structured data', 'records', 'filtering'],
    relatedArticles: ['ent-002', 'ent-003'],
    lastUpdated: '2025-12-01',
    feature: 'entities',
  },
  {
    id: 'ent-002',
    title: 'Filtering and Querying Entities',
    category: 'entities',
    summary: 'How to filter and search entity data',
    content: `
# Filtering and Querying Entities

## Basic Filtering

Use the filter panel to narrow results:

1. Select a field (e.g., "location_id")
2. Choose an operator (equals, contains, etc.)
3. Enter a value
4. Click Apply

## Filter Operators

| Operator | Symbol | Example |
|----------|--------|---------|
| Equals | = | location_id = "MW-01" |
| Not Equals | != | status != "void" |
| Contains | like | analyte like "arsenic" |
| Greater Than | > | concentration > 5.0 |
| Less Than | < | depth < 10.0 |
| Greater/Equal | >= | date >= "2024-01-01" |
| Less/Equal | <= | value <= 100 |
| In List | in | type in ["A", "B"] |

## Combining Filters

Add multiple filters to narrow results:
- All filters are combined with AND logic
- Each filter further restricts results

## Sorting

Click column headers to sort:
- Click once: Ascending (A-Z, 0-9)
- Click again: Descending (Z-A, 9-0)

## Pagination

For large datasets:
- Use page controls at bottom
- Adjust items per page
- Jump to specific pages

## Exporting Results

After filtering:
1. Click **Export**
2. Choose format (CSV, Excel)
3. Download filtered data
    `,
    keywords: ['filter', 'query', 'search', 'entities', 'operators'],
    relatedArticles: ['ent-001', 'ent-003'],
    lastUpdated: '2025-12-01',
    feature: 'entities',
  },
  {
    id: 'ent-003',
    title: 'Exporting Entity Data',
    category: 'entities',
    summary: 'Export entity data to CSV or Excel',
    content: `
# Exporting Entity Data

## Export Formats

| Format | Best For |
|--------|----------|
| CSV | Data analysis, import to other systems |
| Excel | Reports, sharing with non-technical users |
| JSON | API integration, data processing |

## How to Export

1. Navigate to **Entities**
2. Select entity type
3. Apply filters (optional)
4. Click **Export** button
5. Choose format
6. Download file

## Export Options

### All Data
Export all entities of this type

### Filtered Data
Export only entities matching current filters

### Selected Columns
Choose which fields to include

## Tips

- Apply filters before exporting to limit data
- Large exports may take time to generate
- Check the Jobs panel for export status

## Data Format Notes

### CSV
- UTF-8 encoded
- Comma-separated
- Headers in first row

### Excel
- .xlsx format
- Single worksheet
- Headers in first row
- Formatted columns

### JSON
- Array of objects
- ISO date formats
- UTF-8 encoded
    `,
    keywords: ['export', 'csv', 'excel', 'download', 'entities'],
    relatedArticles: ['ent-001', 'ent-002'],
    lastUpdated: '2025-12-01',
    feature: 'entities',
  },

  // ============================================
  // REPORTS
  // ============================================
  {
    id: 'rpt-001',
    title: 'Creating Reports',
    category: 'reports',
    summary: 'How to create AI-assisted reports',
    content: `
# Creating Reports

## Report Types

TraceOS supports several report types:

- **Research Report** - Comprehensive analysis with evidence
- **Executive Summary** - High-level overview
- **Compliance Report** - Regulatory-focused
- **Custom Report** - Based on your questions

## Creating a New Report

1. Go to **Reports** in the sidebar
2. Click **New Report**
3. Select report type
4. Configure sections and questions
5. Click **Generate**

## Report Generation Process

1. **Question Analysis** - System understands each question
2. **Evidence Retrieval** - Searches your documents
3. **Answer Generation** - Creates responses with citations
4. **Quality Check** - Verifies evidence quality
5. **Formatting** - Applies professional formatting

## Report Sections

Most reports include:
- Title Page
- Table of Contents
- Executive Summary
- Main Content (your questions)
- References
- AI Disclosure Statement

## Customizing Questions

Each section can have multiple questions:

\`\`\`
Section: Site Characterization
  Q1: What is the geological setting of the site?
  Q2: What are the key environmental receptors?
\`\`\`

## Monitoring Progress

While generating:
- Watch the progress bar
- See which questions are being answered
- Review partial results

## After Generation

Once complete:
- Review and edit content
- Verify citations
- Export to PDF or Word
    `,
    keywords: ['reports', 'create', 'generate', 'ai', 'documents'],
    relatedArticles: ['rpt-002', 'rpt-003'],
    lastUpdated: '2025-12-01',
    feature: 'reports',
  },
  {
    id: 'rpt-002',
    title: 'Editing Reports',
    category: 'reports',
    summary: 'How to edit and refine generated reports',
    content: `
# Editing Reports

## The Report Editor

After generation, use the editor to refine your report:

- **Content Editing** - Modify text directly
- **Citation Management** - Add or remove citations
- **Section Reordering** - Drag to reorder
- **Formatting** - Apply styles

## Making Edits

### Text Editing
1. Click on any text block
2. Make your changes
3. Changes save automatically

### Adding Citations
1. Select text to cite
2. Click **Add Citation**
3. Search for evidence
4. Select and insert

### Removing Citations
1. Click on a citation number
2. Click **Remove**
3. Citation is removed from text

## Word Optimization

Use the **Optimize** feature to:
- Improve readability
- Ensure professional tone
- Fix grammar and spelling
- Maintain evidence accuracy

## Version History

Every edit creates a version:
- View previous versions
- Compare changes
- Restore if needed

## Collaboration

Share reports with team members:
- View-only access
- Edit access
- Comment access

## Tips

- Edit section by section
- Verify all citations are accurate
- Use the preview before exporting
- Save frequently (auto-save is on)
    `,
    keywords: ['edit', 'reports', 'modify', 'citations', 'refine'],
    relatedArticles: ['rpt-001', 'rpt-003'],
    lastUpdated: '2025-12-01',
    feature: 'reports',
  },
  {
    id: 'rpt-003',
    title: 'Exporting Reports',
    category: 'reports',
    summary: 'Export reports to PDF or Word format',
    content: `
# Exporting Reports

## Export Formats

| Format | Best For |
|--------|----------|
| PDF | Final delivery, printing |
| Word (.docx) | Further editing, client collaboration |
| Markdown | Technical documentation |

## How to Export

1. Open your report
2. Click **Export** button
3. Choose format
4. Configure options
5. Download

## PDF Options

- **Include Cover Page** - Professional title page
- **Include TOC** - Table of contents
- **Include References** - Full reference list
- **Page Numbers** - Add pagination
- **Company Logo** - Add your branding

## Word Options

- **Editable** - Full editing capability
- **Track Changes** - Enable revision mode
- **Comments** - Include review comments
- **Formatting** - Preserve styles

## AI Disclosure

All reports include an AI disclosure statement:

> This document was generated with AI assistance. All content has been derived from source documents with citations provided. Professional review is recommended.

This cannot be removed (compliance requirement).

## Quality Checks

Before exporting, verify:
- [ ] All citations are accurate
- [ ] No placeholder text remains
- [ ] Formatting is correct
- [ ] Cover page information is complete

## After Export

The exported file:
- Downloads to your browser
- Can be shared via email
- Is ready for client delivery
    `,
    keywords: ['export', 'pdf', 'word', 'download', 'reports'],
    relatedArticles: ['rpt-001', 'rpt-002'],
    lastUpdated: '2025-12-01',
    feature: 'reports',
  },

  // ============================================
  // RAG CHAT
  // ============================================
  {
    id: 'rag-001',
    title: 'Using AI Search',
    category: 'rag-chat',
    summary: 'How to effectively search using AI',
    content: `
# Using AI Search

## How It Works

TraceOS uses RAG (Retrieval-Augmented Generation):

1. **Your Question** - You ask in natural language
2. **Evidence Search** - System finds relevant content
3. **Answer Generation** - AI creates response from evidence
4. **Citation** - Every fact links to sources

## Search vs Chat

| Feature | Search | Chat |
|---------|--------|------|
| Purpose | Quick answers | Conversation |
| Context | Single query | Maintains history |
| Best for | Fact finding | Exploration |

## Effective Questions

### Good Questions ✅
- "What is the arsenic concentration at MW-03?"
- "List all PFAS compounds detected above 0.07 µg/L"
- "Summarize the geological setting described in the Phase II ESA"

### Less Effective Questions ❌
- "Tell me everything" (too broad)
- "Is there contamination?" (too vague)
- "What do you think?" (asks for opinion)

## Understanding Results

### Answer
The AI-generated response based solely on your documents.

### Citations [1], [2], [3]
Click these to see the exact source:
- Document name
- Page number
- Excerpt

### Confidence Score
How confident the system is:
- 🟢 High (>0.8) - Strong evidence
- 🟡 Medium (0.5-0.8) - Some evidence
- 🔴 Low (<0.5) - Limited evidence

### "Needs Evidence"
If the system can't find evidence, it will say so rather than hallucinate.

## Tips

1. Be specific about locations, dates, parameters
2. Reference table or figure numbers if known
3. Use quotation marks for exact phrases
4. Try rephrasing if results aren't helpful
    `,
    keywords: ['search', 'rag', 'ai', 'query', 'questions'],
    relatedArticles: ['rag-002', 'gs-003'],
    lastUpdated: '2025-12-01',
    feature: 'rag',
  },
  {
    id: 'rag-002',
    title: 'Chat Conversation Mode',
    category: 'rag-chat',
    summary: 'Using the conversational chat interface',
    content: `
# Chat Conversation Mode

## Overview

Chat mode allows back-and-forth conversation while maintaining context from previous messages.

## Starting a Chat

1. Go to **Chat** in the sidebar
2. Type your first question
3. Review the response
4. Ask follow-up questions

## Conversation Context

The chat remembers previous messages:

> **You:** What wells are at the site?
> 
> **AI:** The site has 15 monitoring wells: MW-01 through MW-15...
> 
> **You:** Which ones showed arsenic above 10 mg/kg?
> 
> **AI:** Based on the lab results, MW-03 and MW-07 showed arsenic above 10 mg/kg...

## Chat vs Search

Use Chat when:
- You need to explore a topic
- Follow-up questions are expected
- Context should carry over

Use Search when:
- You need a quick specific answer
- Each query is independent
- You want to compare multiple results

## Chat Features

### Clear Chat
Start fresh with no context

### Export Chat
Save the conversation as:
- PDF
- Markdown
- Plain text

### Copy Answer
Copy individual responses to clipboard

## Evidence in Chat

Every response includes:
- Inline citations
- Evidence confidence
- Source document links

## Tips

- Start broad, then narrow down
- Reference previous answers by content
- Ask for clarification if needed
- Clear chat when switching topics
    `,
    keywords: ['chat', 'conversation', 'context', 'follow-up'],
    relatedArticles: ['rag-001', 'gs-003'],
    lastUpdated: '2025-12-01',
    feature: 'rag',
  },

  // ============================================
  // SOURCES
  // ============================================
  {
    id: 'src-001',
    title: 'Connecting Database Sources',
    category: 'sources',
    summary: 'How to connect external databases',
    content: `
# Connecting Database Sources

## Supported Databases

| Database | Support Level |
|----------|--------------|
| PostgreSQL | Full |
| MySQL/MariaDB | Full |
| SQL Server | Full |
| Oracle | Beta |

## Adding a New Source

1. Go to **Sources** in the sidebar
2. Click **Add Source**
3. Select database type
4. Enter connection details
5. Test connection
6. Save

## Connection Details

### PostgreSQL/MySQL
\`\`\`
Host: database.example.com
Port: 5432 (PG) or 3306 (MySQL)
Database: your_database
Username: your_user
Password: ********
SSL: Required for production
\`\`\`

## Configuring Sync

After connecting:

1. Select tables to sync
2. Configure PII masking (if needed)
3. Set sync schedule
4. Enable sync

## Sync Options

| Option | Description |
|--------|-------------|
| Manual | Sync on demand |
| Hourly | Every hour |
| Daily | Once per day |
| Real-time | Continuous sync |

## PII Masking

Sensitive fields can be masked:
- Email addresses
- Phone numbers
- Social security numbers
- Custom patterns

## Monitoring Syncs

View sync status:
- Last sync time
- Records synced
- Error messages
- Next scheduled sync

## Troubleshooting

### Connection Failed
- Verify credentials
- Check network/firewall
- Ensure SSL is configured

### Sync Errors
- Check table permissions
- Verify column types
- Review error logs
    `,
    keywords: ['database', 'source', 'connect', 'postgresql', 'mysql', 'sync'],
    relatedArticles: ['src-002', 'jobs-001'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },

  // ============================================
  // JOBS
  // ============================================
  {
    id: 'jobs-001',
    title: 'Understanding Background Jobs',
    category: 'jobs',
    summary: 'How to monitor processing jobs',
    content: `
# Understanding Background Jobs

## What are Jobs?

Jobs are background tasks that process your data:

- **Ingest Jobs** - Parse and index documents
- **Report Jobs** - Generate AI reports
- **Sync Jobs** - Sync database sources
- **Index Jobs** - Rebuild search indices

## Job Status

| Status | Meaning |
|--------|---------|
| ⏳ Queued | Waiting to start |
| 🔄 Processing | Currently running |
| ✅ Completed | Successfully finished |
| ❌ Failed | Error occurred |
| ⏸️ Paused | Temporarily stopped |

## Viewing Jobs

1. Go to **Jobs** in the sidebar
2. See all jobs for current project
3. Filter by type or status
4. Click a job for details

## Job Details

Each job shows:
- Start time and duration
- Progress percentage
- Items processed
- Error messages (if any)

## Retrying Failed Jobs

If a job fails:
1. Click on the job
2. Review the error
3. Fix any issues
4. Click **Retry**

## Canceling Jobs

To stop a running job:
1. Click on the job
2. Click **Cancel**
3. Confirm cancellation

Note: Some jobs may take time to stop safely.

## Job Queue

Jobs are processed in order:
- Earlier jobs first
- Priority jobs may skip queue
- Concurrent processing limits apply

## Tips

- Monitor long-running jobs
- Check failed jobs promptly
- Use job history for debugging
    `,
    keywords: ['jobs', 'processing', 'background', 'queue', 'status'],
    relatedArticles: ['kb-001', 'src-001'],
    lastUpdated: '2025-12-01',
    feature: 'jobs',
  },

  // ============================================
  // ADMIN
  // ============================================
  {
    id: 'adm-001',
    title: 'Team Management',
    category: 'admin',
    summary: 'Managing team members and permissions',
    content: `
# Team Management

## Roles

TraceOS has several role levels:

| Role | Permissions |
|------|-------------|
| Admin | Full access, manage team |
| Editor | Upload, query, create reports |
| Viewer | Read-only access |
| Guest | Limited read access |

## Inviting Team Members

1. Go to **Settings** > **Team**
2. Click **Invite Member**
3. Enter email address
4. Select role
5. Send invitation

## Managing Members

### Change Role
1. Find the member
2. Click **Edit**
3. Select new role
4. Save

### Remove Member
1. Find the member
2. Click **Remove**
3. Confirm removal

## Access Control

### Project-Level
Members can be assigned to specific projects:
- Admin in Project A
- Viewer in Project B

### Tenant-Level
Or given access across all projects:
- Tenant Admin (all projects)
- Tenant Member (assigned projects)

## Invitation Status

| Status | Meaning |
|--------|---------|
| Pending | Invitation sent, not accepted |
| Active | Member has joined |
| Expired | Invitation timed out |

## Security Tips

- Use least-privilege (minimum needed role)
- Review team members regularly
- Remove inactive users promptly
- Use strong passwords
    `,
    keywords: ['team', 'users', 'permissions', 'roles', 'admin'],
    relatedArticles: ['adm-002'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // TROUBLESHOOTING
  // ============================================
  {
    id: 'ts-001',
    title: 'Common Issues and Solutions',
    category: 'troubleshooting',
    summary: 'Solutions to frequently encountered problems',
    content: `
# Common Issues and Solutions

## Search Issues

### "No results found"
**Causes:**
- No documents uploaded
- Documents still processing
- Query too specific

**Solutions:**
- Check Knowledge Base for documents
- Wait for processing to complete
- Try broader search terms

### Results seem irrelevant
**Causes:**
- Insufficient context in query
- Wrong project selected

**Solutions:**
- Add more context to your question
- Verify correct project is selected
- Check document content matches expectations

## Upload Issues

### Upload fails
**Causes:**
- File too large (>100MB default)
- Unsupported file type
- Network timeout

**Solutions:**
- Split large files
- Convert to supported format
- Retry on stable connection

### Document stuck processing
**Causes:**
- Complex document structure
- System busy
- Processing error

**Solutions:**
- Wait (large docs take time)
- Check Jobs panel for errors
- Try re-uploading

## Report Issues

### Report generation fails
**Causes:**
- Insufficient evidence
- Questions too broad
- System timeout

**Solutions:**
- Upload more relevant documents
- Make questions more specific
- Try generating fewer sections

### Citations missing
**Causes:**
- Evidence not found
- Confidence too low

**Solutions:**
- Check source documents exist
- Verify information is in documents

## Connection Issues

### "Session expired"
**Solutions:**
- Refresh the page
- Log in again
- Clear browser cookies

### "API error"
**Solutions:**
- Refresh the page
- Try again in a few minutes
- Contact support if persistent

## Getting Help

If issues persist:
1. Note the error message
2. Check this Help Center
3. Contact support with details
    `,
    keywords: ['troubleshooting', 'errors', 'problems', 'issues', 'solutions'],
    relatedArticles: ['ts-002'],
    lastUpdated: '2025-12-01',
  },
  {
    id: 'ts-002',
    title: 'Performance Tips',
    category: 'troubleshooting',
    summary: 'Tips for better performance',
    content: `
# Performance Tips

## Search Performance

### Faster Searches
- Use specific queries
- Include key terms
- Reference exact values

### Better Results
- Upload high-quality source documents
- Use OCR for scanned documents
- Organize projects logically

## Upload Performance

### Faster Processing
- Keep files under 50MB
- Use native formats (not scans)
- Upload in smaller batches

### Better Extraction
- Use text-based PDFs
- Ensure clear table formatting
- Include searchable text

## Report Performance

### Faster Generation
- Limit sections per report
- Use specific questions
- Ensure evidence is indexed

### Better Quality
- Review and edit results
- Verify all citations
- Use Word optimization

## Browser Tips

### General
- Use modern browsers (Chrome, Firefox, Edge)
- Keep browser updated
- Clear cache periodically

### Memory
- Close unused tabs
- Restart browser if slow
- Use browser dev tools to check issues

## Network Tips

- Use stable internet connection
- Avoid VPNs if causing issues
- Check firewall settings
    `,
    keywords: ['performance', 'speed', 'tips', 'optimization'],
    relatedArticles: ['ts-001'],
    lastUpdated: '2025-12-01',
  },

  // ============================================
  // PROJECT MANAGEMENT
  // ============================================
  {
    id: 'gs-004',
    title: 'Managing Projects',
    category: 'getting-started',
    summary: 'Create, configure, and manage your projects',
    content: `
# Managing Projects

## What is a Project?

A project is your workspace for a specific task or client engagement:
- Contains documents, entities, and reports
- Has its own search indices
- Can have specific policy packs applied

## Creating a New Project

1. Click the **Project Selector** in the header
2. Click **+ New Project**
3. Enter project details:
   - **Name**: Descriptive name (e.g., "Site Assessment 2025")
   - **Description**: Brief summary of the project scope
4. Click **Create**

## Project Configuration

### Policy Packs
Assign a policy pack to enforce compliance rules:
1. Go to **Settings** > **Project Settings**
2. Select a policy pack from the dropdown
3. Save changes

### Access Control
Control who can access your project:
- **Admin**: Full control
- **Editor**: Upload and modify
- **Viewer**: Read-only access

## Switching Projects

Use the Project Selector dropdown to:
- Switch between projects
- See recent projects
- Search for projects by name

## Archiving Projects

To archive a completed project:
1. Go to **Settings** > **Project Settings**
2. Click **Archive Project**
3. Confirm the action

Archived projects are read-only but can be unarchived later.

## Best Practices

- Use clear, descriptive project names
- One project per client/site/engagement
- Archive completed projects to keep workspace clean
- Assign appropriate access levels
    `,
    keywords: ['project', 'create', 'manage', 'configure', 'settings'],
    relatedArticles: ['gs-001', 'gs-002', 'adm-001'],
    lastUpdated: '2025-12-01',
    feature: 'projects',
  },

  // ============================================
  // TENANT MANAGEMENT
  // ============================================
  {
    id: 'adm-002',
    title: 'Tenant Administration',
    category: 'admin',
    summary: 'Managing tenants and organization settings',
    content: `
# Tenant Administration

## What is a Tenant?

A tenant represents your organization in TraceOS:
- Completely isolated from other organizations
- Contains all your projects, users, and data
- Has its own configuration and settings

## Tenant Settings

### General Settings
Access via **Settings** > **Tenant Settings**:
- **Name**: Organization display name
- **Description**: Organization description
- **Logo**: Upload custom branding
- **Contact Email**: Primary contact email

### Security Settings
- **Session Timeout**: Auto-logout duration
- **Password Policy**: Minimum requirements
- **2FA Requirement**: Enforce two-factor auth
- **IP Allowlist**: Restrict access by IP

### Data Settings
- **Retention Policy**: How long to keep data
- **Backup Frequency**: Automated backup schedule
- **Export Restrictions**: Control data export

## User Management

### Inviting Users
1. Go to **Settings** > **Team**
2. Click **Invite User**
3. Enter email and select role
4. Send invitation

### User Roles
| Role | Description |
|------|-------------|
| Tenant Admin | Full organization access |
| Project Admin | Manage assigned projects |
| Editor | Upload and query |
| Viewer | Read-only access |

## Billing & Usage

View usage metrics:
- Documents uploaded
- Queries performed
- Storage used
- API calls

## API Keys

Generate API keys for integrations:
1. Go to **Settings** > **API Keys**
2. Click **Generate New Key**
3. Copy and store securely
4. Set expiration and permissions

## Audit Logs

View all tenant activity:
- User actions
- Document uploads
- Query history
- Configuration changes
    `,
    keywords: ['tenant', 'organization', 'admin', 'settings', 'management'],
    relatedArticles: ['adm-001', 'gs-004'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // KEYBOARD SHORTCUTS
  // ============================================
  {
    id: 'gs-005',
    title: 'Keyboard Shortcuts',
    category: 'getting-started',
    summary: 'Quick keyboard shortcuts for efficient navigation',
    content: `
# Keyboard Shortcuts

Master these shortcuts to work faster in TraceOS.

## Global Shortcuts

| Shortcut | Action |
|----------|--------|
| **?** | Open Help Center |
| **Ctrl/Cmd + K** | Quick search / Command palette |
| **Ctrl/Cmd + /** | Toggle sidebar |
| **Esc** | Close modal / Cancel action |

## Navigation

| Shortcut | Action |
|----------|--------|
| **G then H** | Go to Home |
| **G then K** | Go to Knowledge Base |
| **G then E** | Go to Entities |
| **G then R** | Go to Reports |
| **G then S** | Go to Search |
| **G then J** | Go to Jobs |

## Search & Chat

| Shortcut | Action |
|----------|--------|
| **/** | Focus search input |
| **Enter** | Submit query |
| **Up/Down** | Navigate history |
| **Ctrl/Cmd + Enter** | Search with current filters |

## Document Viewer

| Shortcut | Action |
|----------|--------|
| **+/-** | Zoom in/out |
| **Arrow Keys** | Navigate pages |
| **Ctrl/Cmd + F** | Find in document |

## Report Editor

| Shortcut | Action |
|----------|--------|
| **Ctrl/Cmd + S** | Save report |
| **Ctrl/Cmd + B** | Bold text |
| **Ctrl/Cmd + I** | Italic text |
| **Ctrl/Cmd + Shift + C** | Add citation |

## Table Navigation

| Shortcut | Action |
|----------|--------|
| **Tab** | Next cell |
| **Shift + Tab** | Previous cell |
| **Ctrl/Cmd + A** | Select all |
| **Ctrl/Cmd + C** | Copy selection |

## Tips

- Press **?** anytime to see this list
- Most shortcuts follow common patterns
- Use **Esc** to cancel any action
- Enable "Show shortcut hints" in settings
    `,
    keywords: ['keyboard', 'shortcuts', 'hotkeys', 'navigation', 'tips'],
    relatedArticles: ['gs-002', 'ts-002'],
    lastUpdated: '2025-12-01',
  },

  // ============================================
  // DATA EXPORT
  // ============================================
  {
    id: 'ent-004',
    title: 'Data Export & Formats',
    category: 'entities',
    summary: 'Export data in various formats',
    content: `
# Data Export & Formats

## Available Export Formats

### CSV (Comma-Separated Values)
- Universal format for spreadsheets
- UTF-8 encoded for international characters
- Headers in first row
- Best for: Excel, Google Sheets, data analysis

### Excel (.xlsx)
- Native Microsoft Excel format
- Preserves formatting
- Multiple worksheets supported
- Best for: Business reports, sharing with clients

### JSON (JavaScript Object Notation)
- Structured data format
- Preserves data types
- Best for: API integration, programming

### PDF (for Reports)
- Formatted document output
- Includes cover page and citations
- Best for: Final deliverables

## Export Locations

### Entity Data
1. Navigate to **Entities**
2. Apply filters (optional)
3. Click **Export**
4. Choose format and download

### Report Content
1. Open the report
2. Click **Export**
3. Select PDF or Word
4. Download

### Audit Logs
1. Go to **Settings** > **Audit**
2. Select date range
3. Click **Export Logs**
4. Download CSV

## Bulk Export

For large datasets:
1. Select multiple items
2. Click **Bulk Export**
3. Choose format
4. Job starts in background
5. Download link emailed when ready

## Data Format Details

### Date/Time Format
- ISO 8601: \`2025-01-15T14:30:00Z\`
- Excel: Native date format
- CSV: ISO string

### Numbers
- Preserved precision
- No formatting applied
- Use locale-appropriate settings

### Special Characters
- UTF-8 encoding
- Proper escaping in CSV
- Unicode support

## Export Limits

| Type | Limit |
|------|-------|
| Entity rows | 100,000 per export |
| Report size | 50 MB |
| Concurrent exports | 3 per user |
    `,
    keywords: ['export', 'download', 'csv', 'excel', 'json', 'pdf', 'format'],
    relatedArticles: ['ent-003', 'rpt-003'],
    lastUpdated: '2025-12-01',
    feature: 'entities',
  },

  // ============================================
  // DATABASE CONNECTORS
  // ============================================
  {
    id: 'src-002',
    title: 'Database Connector Setup',
    category: 'sources',
    summary: 'Connect and sync external databases',
    content: `
# Database Connector Setup

## Overview

Database connectors allow you to:
- Pull data from external databases
- Keep TraceOS in sync automatically
- Query external data alongside documents

## Supported Databases

| Database | Version | Features |
|----------|---------|----------|
| PostgreSQL | 10+ | Full support, PII masking |
| MySQL | 5.7+ | Full support, PII masking |
| SQL Server | 2016+ | Full support |
| Oracle | 12c+ | Beta support |

## Setup Steps

### 1. Prepare Database Access

Ensure you have:
- Host/endpoint address
- Port number
- Database name
- Username with read access
- Password
- SSL certificate (if required)

### 2. Create Connection

1. Go to **Sources** > **Add Source**
2. Select database type
3. Enter connection details:
   \`\`\`
   Host: db.example.com
   Port: 5432
   Database: analytics
   Username: traceos_read
   Password: ********
   SSL Mode: require
   \`\`\`
4. Click **Test Connection**
5. Save

### 3. Configure Tables

Select which tables to sync:
- Choose tables from the list
- Map columns to entity types
- Configure PII masking rules

### 4. Set Sync Schedule

| Schedule | When |
|----------|------|
| Manual | On-demand only |
| Hourly | Every hour |
| Daily | Once per day (recommended) |
| Real-time | Continuous (premium) |

## PII Masking

Protect sensitive data:
1. Identify PII columns
2. Choose masking method:
   - Hash (one-way)
   - Redact (replace with ***)
   - Pseudonymize (replace with fake data)

## Text Generation

Convert rows to searchable text:
\`\`\`
Template: "Sample {sample_id} collected at {location} 
          on {date} showed {analyte}: {value} {unit}"
\`\`\`

## Troubleshooting

### Connection Refused
- Check host/port
- Verify firewall rules
- Ensure database allows remote connections

### Authentication Failed
- Verify credentials
- Check user permissions
- Reset password if needed

### Sync Errors
- Check table permissions
- Verify column mappings
- Review error logs
    `,
    keywords: ['database', 'connector', 'postgresql', 'mysql', 'sync', 'source'],
    relatedArticles: ['src-001', 'jobs-001'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },

  // ============================================
  // API & WEBHOOKS
  // ============================================
  {
    id: 'src-003',
    title: 'API Integration & Webhooks',
    category: 'sources',
    summary: 'Integrate TraceOS with external systems',
    content: `
# API Integration & Webhooks

## API Overview

TraceOS provides a RESTful API for programmatic access:
- Upload documents
- Run queries
- Manage entities
- Generate reports

## Authentication

### API Keys
1. Go to **Settings** > **API Keys**
2. Click **Generate New Key**
3. Copy the key (shown only once)
4. Use in Authorization header:
   \`\`\`
   Authorization: Bearer your-api-key
   \`\`\`

### JWT Tokens
For user-based access:
1. Call \`POST /auth/login\`
2. Receive JWT token
3. Include in requests

## Common Endpoints

### Documents
\`\`\`
POST /projects/{id}/artifacts    # Upload document
GET  /projects/{id}/artifacts    # List documents
GET  /artifacts/{id}             # Get document details
\`\`\`

### RAG Query
\`\`\`
POST /rag/query
{
  "question": "What is the arsenic level?",
  "project_id": "uuid",
  "top_k": 10
}
\`\`\`

### Entities
\`\`\`
GET /projects/{id}/entities?type=sample_result
GET /entities/{id}
\`\`\`

## Webhooks

Receive notifications when events occur.

### Supported Events
- \`artifact.uploaded\` - Document uploaded
- \`artifact.processed\` - Processing complete
- \`job.completed\` - Job finished
- \`job.failed\` - Job error
- \`report.generated\` - Report ready

### Creating a Webhook
1. Go to **Settings** > **Webhooks**
2. Click **Add Webhook**
3. Configure:
   - **URL**: Your endpoint
   - **Events**: Select events
   - **Secret**: For signature verification
4. Save

### Webhook Payload
\`\`\`json
{
  "event": "artifact.processed",
  "timestamp": "2025-01-15T10:30:00Z",
  "data": {
    "artifact_id": "uuid",
    "project_id": "uuid",
    "chunks_created": 42
  }
}
\`\`\`

### Signature Verification
Verify webhook authenticity:
\`\`\`
X-TraceOS-Signature: sha256=...
\`\`\`

## Rate Limits

| Tier | Requests/min |
|------|--------------|
| Free | 60 |
| Pro | 300 |
| Enterprise | Custom |

## SDKs

Official SDKs available:
- Python: \`pip install traceos\`
- TypeScript: \`npm install @traceos/sdk\`
    `,
    keywords: ['api', 'webhook', 'integration', 'rest', 'sdk'],
    relatedArticles: ['src-001', 'src-002'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // EVIDENCE & CITATIONS
  // ============================================
  {
    id: 'rag-003',
    title: 'Understanding Evidence Citations',
    category: 'rag-chat',
    summary: 'How the citation system works',
    content: `
# Understanding Evidence Citations

## The Citation Principle

TraceOS follows a strict principle:
> **"If it isn't cited, it isn't said"**

Every answer must be backed by evidence from your documents.

## How Citations Work

### Citation Format
Citations appear as numbered references:
> The arsenic concentration was 15.2 mg/kg **[1]**, exceeding the 
> residential guideline of 12 mg/kg **[2]**.

### Citation Components
Each citation includes:
- **Document Name**: Source file
- **Page Number**: Exact location
- **Excerpt**: Relevant text passage
- **Confidence**: Match quality

## Viewing Citations

### Click to Expand
Click any citation number to see:
1. Source document thumbnail
2. Highlighted excerpt
3. Page location
4. Option to view full document

### Citation Panel
Toggle the citation panel to see all references:
- List of all citations
- Document breakdown
- Evidence strength indicators

## Evidence Types

| Type | Icon | Description |
|------|------|-------------|
| Table Data | 📊 | Values from tables |
| Figure | 📈 | Charts and diagrams |
| Text | 📝 | Narrative content |
| Regulatory | ⚖️ | Guidelines and standards |

## Confidence Scores

### High Confidence (80-100%)
- Direct quote or exact match
- Multiple supporting sources
- Clear, unambiguous evidence

### Medium Confidence (50-79%)
- Paraphrased content
- Single source
- Some interpretation required

### Low Confidence (Below 50%)
- Indirect evidence
- Related but not exact
- May need verification

## "Needs Evidence"

When the system says "Needs Evidence":
- Information not found in documents
- Query too specific or broad
- Data may be in unprocessed documents

**Solutions:**
1. Upload additional documents
2. Rephrase your question
3. Check document processing status

## Best Practices

✅ **Always verify important citations**
- Click through to source
- Read surrounding context
- Cross-reference with other sources

✅ **Use citation counts as quality indicator**
- More citations = more confidence
- Single citations = verify carefully

✅ **Report questionable citations**
- Use feedback feature
- Helps improve system accuracy
    `,
    keywords: ['citation', 'evidence', 'reference', 'source', 'verification'],
    relatedArticles: ['rag-001', 'rag-002', 'gs-003'],
    lastUpdated: '2025-12-01',
    feature: 'rag',
  },

  // ============================================
  // POLICY PACKS
  // ============================================
  {
    id: 'adm-003',
    title: 'Policy Packs & Compliance',
    category: 'admin',
    summary: 'Enforce compliance rules and SOP guardrails',
    content: `
# Policy Packs & Compliance

## What are Policy Packs?

Policy packs are rule sets that:
- Enforce compliance requirements
- Guide AI responses
- Ensure professional standards
- Add disclosure statements

## Built-in Policy Packs

### EGBC (Engineers and Geoscientists BC)
- Professional practice requirements
- Limitations statements
- Seal requirements
- AI disclosure language

### Environmental Assessment
- Regulatory guideline references
- Standard citation formats
- Uncertainty statements
- Scope limitations

### Legal/Compliance
- Disclaimer requirements
- Confidentiality notices
- Professional liability limits

## Applying Policy Packs

### To a Project
1. Go to **Settings** > **Project Settings**
2. Select **Policy Pack**
3. Choose from available packs
4. Save changes

### Default Policy
Set a default for new projects:
1. Go to **Tenant Settings**
2. Set **Default Policy Pack**

## Policy Pack Components

### Disclosure Templates
Standard text added to outputs:
\`\`\`
This report was generated with AI assistance. 
All findings are based on cited evidence. 
Professional review is recommended.
\`\`\`

### Response Rules
- Confidence thresholds
- Citation requirements
- Uncertainty language
- Scope limitations

### Validation Rules
- Required fields
- Format checks
- Compliance flags

## Custom Policy Packs

Create custom packs for your organization:
1. Go to **Settings** > **Policy Packs**
2. Click **Create New**
3. Configure:
   - Name and description
   - Disclosure template
   - Response rules
   - Validation rules
4. Save and apply

## Compliance Features

### Audit Trail
- All queries logged
- Evidence chain preserved
- User actions tracked

### Retention Policies
- Data lifecycle management
- Automatic archival
- Compliance with regulations

### Export Controls
- Watermarking
- Metadata inclusion
- Version tracking
    `,
    keywords: ['policy', 'compliance', 'egbc', 'disclosure', 'rules', 'sop'],
    relatedArticles: ['adm-001', 'adm-002', 'rpt-001'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // THEME & SETTINGS
  // ============================================
  {
    id: 'gs-006',
    title: 'Theme & Display Settings',
    category: 'getting-started',
    summary: 'Customize your TraceOS experience',
    content: `
# Theme & Display Settings

## Dark Mode

### Toggle Dark Mode
- Click the sun/moon icon in the header
- Or go to **Settings** > **Appearance**
- Or use keyboard shortcut **Ctrl/Cmd + Shift + D**

### System Preference
Follow your OS setting:
1. Go to **Settings** > **Appearance**
2. Select **System** under Theme
3. Theme changes with your OS

## Display Options

### Density
Control spacing and padding:
- **Comfortable**: More whitespace (default)
- **Compact**: Tighter layout, more data visible

### Font Size
Adjust text size:
- Small (12px)
- Medium (14px) - default
- Large (16px)

### Sidebar
- **Expanded**: Full labels visible
- **Collapsed**: Icons only
- **Auto-collapse**: Collapses on small screens

## Table Settings

### Default Page Size
Set how many rows to show:
- 10, 25, 50, or 100 rows

### Column Visibility
- Click column header menu
- Toggle column visibility
- Reorder columns by dragging

### Number Formatting
- Decimal places
- Thousand separators
- Scientific notation

## Language & Region

### Language
Currently supported:
- English (US)
- English (UK)
- French (Canada)

### Date Format
- MM/DD/YYYY (US)
- DD/MM/YYYY (International)
- YYYY-MM-DD (ISO)

### Number Format
- 1,234.56 (US)
- 1.234,56 (European)

## Notifications

### In-App Notifications
- Job completions
- Upload status
- System announcements

### Email Notifications
- Daily summary
- Critical alerts
- Report ready notices

## Accessibility

### High Contrast
Enable higher contrast for visibility

### Reduce Motion
Minimize animations

### Screen Reader Support
Full ARIA label support
    `,
    keywords: ['theme', 'dark mode', 'settings', 'display', 'appearance', 'accessibility'],
    relatedArticles: ['gs-001', 'gs-002'],
    lastUpdated: '2025-12-01',
  },

  // ============================================
  // OCR & PROCESSING
  // ============================================
  {
    id: 'kb-003',
    title: 'OCR & Document Processing',
    category: 'knowledge-base',
    summary: 'How documents are processed and indexed',
    content: `
# OCR & Document Processing

## Processing Pipeline

When you upload a document:

### 1. File Validation
- Format check
- Size verification
- Virus scan
- Duplicate detection

### 2. Text Extraction
- Native text extraction (text-based PDFs)
- OCR for images and scanned documents
- Table detection and extraction

### 3. Layout Analysis
- Page structure detection
- Table identification
- Figure/chart recognition
- Header/footer removal

### 4. Chunking
Content split into semantic units:
- Paragraph-based
- Table-aware
- Figure-linked

### 5. Embedding
AI creates semantic vectors:
- Captures meaning, not just keywords
- Enables similarity search
- Multi-language support

### 6. Indexing
Added to search indices:
- Evidence index (tables/figures)
- Narrative index (text)
- Entity index (structured data)

## OCR (Optical Character Recognition)

### When OCR is Used
- Scanned PDFs
- Image files (PNG, JPG)
- Photos of documents
- Poor-quality PDFs

### OCR Quality Factors
- Image resolution (300 DPI minimum)
- Text clarity and contrast
- Language complexity
- Handwriting vs. typed

### Improving OCR Results
1. **Use higher resolution scans**
   - 300 DPI minimum
   - 600 DPI for complex documents

2. **Ensure good contrast**
   - Black text on white background
   - Avoid colored backgrounds

3. **Straighten images**
   - Deskew before upload
   - Use document scanner apps

## Table Extraction

### Supported Table Types
- Simple grid tables
- Tables with merged cells
- Multi-page tables
- Tables in columns

### Table Quality Tips
- Clear borders help extraction
- Avoid nested tables
- Keep headers consistent

## Processing Status

Check processing in **Jobs** panel:

| Status | Meaning |
|--------|---------|
| Queued | Waiting to start |
| Parsing | Extracting text |
| Chunking | Splitting content |
| Embedding | Creating vectors |
| Indexing | Adding to search |
| Complete | Ready to query |

## Re-processing

To re-process a document:
1. Go to **Knowledge Base**
2. Select the document
3. Click **Re-process**
4. Wait for completion

Useful after:
- System updates
- OCR improvements
- Table extraction changes
    `,
    keywords: ['ocr', 'processing', 'extraction', 'scan', 'pdf', 'table'],
    relatedArticles: ['kb-001', 'kb-002', 'jobs-001'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },

  // ============================================
  // BATCH UPLOAD
  // ============================================
  {
    id: 'kb-004',
    title: 'Batch Upload & Processing',
    category: 'knowledge-base',
    summary: 'Upload and process multiple documents at once',
    content: `
# Batch Upload & Processing

## Batch Upload Methods

### Drag and Drop
1. Go to **Knowledge Base**
2. Click **Bulk Upload** or drag files to the page
3. Drop multiple files at once
4. Processing begins automatically

### Folder Upload
1. Click **Upload Folder**
2. Select a folder
3. All supported files are uploaded
4. Folder structure preserved in metadata

### From Data Source
For database or cloud sources:
1. Configure the source
2. Click **Sync All**
3. Records converted to artifacts

## Supported Files

| Type | Extensions | Max Size |
|------|------------|----------|
| PDF | .pdf | 100 MB |
| Word | .docx, .doc | 50 MB |
| Excel | .xlsx, .xls, .csv | 50 MB |
| PowerPoint | .pptx | 100 MB |
| Images | .png, .jpg, .tiff | 25 MB |
| CAD | .dwg, .dxf | 100 MB |

## Processing Queue

### Queue Management
Files are processed in order:
- Priority: Most recent first
- Large files take longer
- Multiple files in parallel

### Monitoring Progress
Track batch progress:
1. Go to **Jobs** panel
2. See overall batch progress
3. Click individual items for details

### Canceling Batch
To stop a batch upload:
1. Go to **Jobs** panel
2. Find the batch job
3. Click **Cancel Batch**
4. Already processed files remain

## Tips for Large Batches

### Organize First
- Group related documents
- Name files descriptively
- Remove duplicates beforehand

### Optimal Batch Size
- 50-100 files per batch
- Split very large batches
- Monitor progress

### File Quality
- Use original files when possible
- Avoid low-resolution scans
- Ensure files aren't corrupted

## Error Handling

### Common Issues
- **File too large**: Split or compress
- **Unsupported format**: Convert first
- **Corrupt file**: Re-create or skip

### Failed Files
View failed uploads:
1. Go to **Jobs** panel
2. Filter by **Failed**
3. See error messages
4. Retry or skip

### Retry Options
- **Retry Single**: Process one file again
- **Retry All Failed**: Process all failures
- **Skip**: Mark as skipped and continue
    `,
    keywords: ['batch', 'bulk', 'upload', 'multiple', 'folder', 'queue'],
    relatedArticles: ['kb-001', 'kb-002', 'jobs-001'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },

  // ============================================
  // QUERY HISTORY & CACHE
  // ============================================
  {
    id: 'rag-004',
    title: 'Query History & Caching',
    category: 'rag-chat',
    summary: 'View past queries and understand caching',
    content: `
# Query History & Caching

## Query History

### Accessing History
1. Go to **Search** or **Chat**
2. Click the **History** icon
3. Browse past queries

### History Features
- Search within history
- Filter by date range
- Sort by recency or frequency
- Re-run past queries

### History Retention
- Last 1000 queries stored
- Older queries archived
- Export history available

## Query Caching

### How Caching Works
Identical queries return cached results:
1. Query submitted
2. Cache checked first
3. If hit: instant response
4. If miss: full RAG pipeline

### Cache Benefits
- Faster responses
- Consistent results
- Reduced API costs

### Cache Indicators
- ⚡ **Cached**: From cache
- 🔄 **Fresh**: New query

## Cache Settings

### Cache Duration
Default: 24 hours

To adjust:
1. Go to **Settings** > **Project**
2. Set **Cache TTL**
3. Options: 1h, 6h, 24h, 7d

### Clearing Cache
Force fresh results:
1. Click **Refresh** on a query
2. Or clear project cache in settings

### Cache Invalidation
Cache automatically clears when:
- New documents uploaded
- Documents re-processed
- Index rebuilt

## Re-running Queries

### From History
1. Find query in history
2. Click to view results
3. Click **Re-run** for fresh results

### Query Variations
Try variations:
- Different wording
- Added context
- Specific filters

## Export History

### Export Options
- **CSV**: Query and response text
- **JSON**: Full metadata included

### Export Steps
1. Go to **Search History**
2. Select queries (or all)
3. Click **Export**
4. Choose format
5. Download

## Privacy Notes

- History is per-user
- Admins can view team history
- Export for compliance
- Delete individual queries
    `,
    keywords: ['history', 'cache', 'query', 'search', 'past', 'recent'],
    relatedArticles: ['rag-001', 'rag-002', 'gs-003'],
    lastUpdated: '2025-12-01',
    feature: 'rag',
  },

  // ============================================
  // REPORT TEMPLATES
  // ============================================
  {
    id: 'rpt-004',
    title: 'Report Templates & Customization',
    category: 'reports',
    summary: 'Create and customize report templates',
    content: `
# Report Templates & Customization

## Built-in Templates

### Research Report
Comprehensive analysis with:
- Executive summary
- Detailed findings
- Full citations
- Appendices

### Executive Summary
High-level overview:
- Key findings
- Recommendations
- Action items

### Compliance Report
Regulatory-focused:
- Guideline comparisons
- Exceedance summaries
- Compliance status

### Technical Memo
Brief technical format:
- Purpose
- Methods
- Findings
- Conclusions

## Customizing Templates

### Template Editor
1. Go to **Reports** > **Templates**
2. Click **Create Template** or edit existing
3. Configure sections

### Section Types
- **Question Section**: AI-generated answer
- **Static Text**: Fixed content
- **Variable**: Dynamic data insertion
- **Table**: Structured data display

### Variables Available
\`\`\`
{{project_name}}      - Project name
{{client_name}}       - Client name
{{date}}              - Current date
{{author}}            - Report author
{{evidence_count}}    - Citations count
{{confidence_avg}}    - Average confidence
\`\`\`

## Styling Options

### Cover Page
Customize the title page:
- Logo placement
- Title formatting
- Subtitle and date
- Author information

### Headers & Footers
Add consistent headers/footers:
- Page numbers
- Company name
- Document title
- Confidentiality notice

### Typography
- Font selection (limited set)
- Heading styles
- Body text size
- Line spacing

## Branding

### Organization Branding
Apply consistent branding:
1. Go to **Settings** > **Branding**
2. Upload logo
3. Set primary colors
4. Configure footer text

### Per-Report Overrides
Override for specific reports:
1. Open report settings
2. Click **Branding**
3. Customize as needed

## Template Library

### Saving Templates
1. Configure report sections
2. Click **Save as Template**
3. Name and describe
4. Save

### Sharing Templates
- Share within tenant
- Export as file
- Import from file

### Template Versioning
- Track changes over time
- Revert to previous versions
- Compare versions
    `,
    keywords: ['template', 'report', 'customize', 'branding', 'style'],
    relatedArticles: ['rpt-001', 'rpt-002', 'rpt-003'],
    lastUpdated: '2025-12-01',
    feature: 'reports',
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  {
    id: 'adm-004',
    title: 'Notifications & Alerts',
    category: 'admin',
    summary: 'Configure notifications and alert rules',
    content: `
# Notifications & Alerts

## Notification Types

### System Notifications
- Processing complete
- Job failures
- System maintenance
- Feature updates

### Compliance Alerts
- Threshold exceedances
- Deadline approaching
- Missing data warnings

### Activity Notifications
- Team member actions
- Report shares
- Document uploads

## Notification Channels

### In-App
- Bell icon in header
- Real-time updates
- Click to view details

### Email
- Daily digest option
- Immediate critical alerts
- Configurable frequency

### Webhooks
- Custom integrations
- POST to your endpoint
- Include full payload

## Configuring Notifications

### Personal Settings
1. Go to **Settings** > **Notifications**
2. Toggle notification types:
   - [ ] Job completions
   - [x] Job failures (critical)
   - [x] Reports ready
   - [ ] Team activity

### Email Preferences
- **Immediate**: Get emails as they happen
- **Digest**: Daily summary
- **Critical Only**: Only failures/errors

## Alert Rules

### Creating Alert Rules
Set up automated alerts:
1. Go to **Settings** > **Alert Rules**
2. Click **Create Rule**
3. Configure:
   - Trigger condition
   - Threshold value
   - Notification method
4. Save and activate

### Example Rules
\`\`\`
IF sample_result.arsenic > 12 mg/kg
THEN Alert: "Arsenic exceedance detected"
SEND TO: email, in-app
\`\`\`

### Rule Types
- **Threshold**: Value exceeds limit
- **Deadline**: Date approaching
- **Pattern**: Unusual activity
- **Missing Data**: Expected data not found

## Managing Alerts

### Alert History
View past alerts:
- Date and time
- Trigger details
- Actions taken
- Resolution status

### Muting Alerts
Temporarily silence:
1. Click alert
2. Select **Mute**
3. Set duration
4. Confirm

### Bulk Actions
- Acknowledge all
- Clear read notifications
- Export to CSV
    `,
    keywords: ['notification', 'alert', 'email', 'rules', 'threshold'],
    relatedArticles: ['adm-001', 'adm-002'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // SECURITY
  // ============================================
  {
    id: 'adm-005',
    title: 'Security Best Practices',
    category: 'admin',
    summary: 'Keep your data safe and secure',
    content: `
# Security Best Practices

## Account Security

### Strong Passwords
Requirements:
- Minimum 12 characters
- Mix of upper and lowercase
- Include numbers and symbols
- No common words or patterns

### Two-Factor Authentication
Enable 2FA:
1. Go to **Profile** > **Security**
2. Click **Enable 2FA**
3. Scan QR code with authenticator app
4. Enter verification code
5. Save backup codes

### Session Management
- Review active sessions
- Revoke unknown sessions
- Set session timeout

## API Key Security

### Key Management
- Generate separate keys for different uses
- Set expiration dates
- Limit permissions per key
- Rotate keys regularly

### Secure Storage
Never:
- Commit keys to version control
- Share keys in emails/chat
- Store in plain text

Do:
- Use environment variables
- Use secrets managers
- Encrypt at rest

## Data Protection

### Encryption
- Data encrypted in transit (TLS 1.3)
- Data encrypted at rest (AES-256)
- End-to-end for sensitive fields

### Access Control
- Role-based access (RBAC)
- Project-level permissions
- Audit all access

### Data Isolation
- Tenant data completely isolated
- Separate databases per tenant (enterprise)
- No cross-tenant data leakage

## Compliance

### Audit Logging
All actions logged:
- User identity
- Action type
- Timestamp
- Resource affected

### Data Retention
Configure retention:
- Set retention periods
- Automatic archival
- Secure deletion

### Export Controls
- Watermark exports
- Include metadata
- Track downloads

## Security Checklist

✅ **Users**
- [ ] Enable 2FA for all users
- [ ] Review user list monthly
- [ ] Remove inactive accounts
- [ ] Use least-privilege roles

✅ **API Keys**
- [ ] Rotate keys quarterly
- [ ] Audit key usage
- [ ] Set expiration dates
- [ ] Document key purposes

✅ **Data**
- [ ] Review sharing settings
- [ ] Enable audit logging
- [ ] Configure retention
- [ ] Test backup/restore

## Incident Response

If you suspect a breach:
1. Change your password immediately
2. Revoke all API keys
3. Review audit logs
4. Contact support@alpyne.ai
5. Document the incident
    `,
    keywords: ['security', 'password', '2fa', 'encryption', 'audit', 'compliance'],
    relatedArticles: ['adm-001', 'adm-002', 'adm-003'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // FAQ
  // ============================================
  {
    id: 'ts-003',
    title: 'Frequently Asked Questions',
    category: 'troubleshooting',
    summary: 'Answers to common questions',
    content: `
# Frequently Asked Questions

## General

### What is TraceOS?
TraceOS is an evidence-driven ETL + RAG platform that helps you search, analyze, and generate reports from your documents with full citation tracking.

### What does "evidence-driven" mean?
Every answer in TraceOS must be backed by evidence from your documents. If the system can't find evidence, it will say so rather than guess.

### What file types are supported?
PDF, Word (.docx), Excel (.xlsx, .csv), PowerPoint (.pptx), images (PNG, JPG), and CAD files (DWG, DXF).

### How secure is my data?
Very secure. Data is encrypted in transit and at rest, tenants are isolated, and all access is logged. Enterprise customers can have dedicated infrastructure.

## Search & RAG

### Why does the AI say "Needs Evidence"?
This means the system couldn't find relevant information in your uploaded documents. Try uploading more documents or rephrasing your question.

### How do I improve search results?
- Upload more relevant documents
- Use specific questions
- Include context (locations, dates, values)
- Check that documents are fully processed

### What's the difference between Search and Chat?
Search is for quick, one-off queries. Chat maintains conversation context for follow-up questions.

## Documents

### How long does processing take?
Most documents process in 1-5 minutes. Large PDFs (100+ pages) or scanned documents may take longer.

### Can I upload scanned documents?
Yes! TraceOS uses OCR to extract text from scanned documents and images. Higher resolution scans give better results.

### Why wasn't my table extracted correctly?
Table extraction works best with:
- Clear borders
- Consistent formatting
- Standard grid layouts
Try re-uploading with better scan quality.

## Reports

### Can I edit the AI-generated content?
Yes! After generation, you can edit any text, add or remove citations, and reorder sections.

### Why is a section blank?
The AI couldn't find sufficient evidence to answer that question. You may need to upload more documents or adjust the question.

### Can I use my own branding?
Yes, enterprise customers can configure custom logos, colors, and footer text.

## Billing & Limits

### What counts toward my usage?
- Documents uploaded (storage)
- Queries performed (API calls)
- Reports generated

### Are there rate limits?
Yes, based on your plan:
- Free: 60 requests/minute
- Pro: 300 requests/minute
- Enterprise: Custom limits

## Technical

### Does TraceOS work offline?
No, TraceOS requires internet connectivity to function.

### What browsers are supported?
Chrome, Firefox, Edge, and Safari (latest versions).

### Can I use the API?
Yes! Full REST API available. Generate API keys in Settings.
    `,
    keywords: ['faq', 'questions', 'help', 'common', 'answers'],
    relatedArticles: ['gs-001', 'ts-001', 'ts-002'],
    lastUpdated: '2025-12-01',
  },

  // ============================================
  // AI CONFIDENCE
  // ============================================
  {
    id: 'rag-005',
    title: 'Understanding AI Confidence Scores',
    category: 'rag-chat',
    summary: 'How to interpret confidence scores in answers',
    content: `
# Understanding AI Confidence Scores

## What is a Confidence Score?

Every answer in TraceOS includes a confidence score (0-100%) indicating how well the evidence supports the response.

## Score Interpretation

### 🟢 High Confidence (80-100%)

**What it means:**
- Strong, direct evidence found
- Multiple supporting sources
- Clear, unambiguous match

**Example:**
> "The arsenic concentration at MW-03 was 15.2 mg/kg [1]."
> Confidence: 95%

**Action:** Generally reliable, but verify for critical decisions.

### 🟡 Medium Confidence (50-79%)

**What it means:**
- Evidence found but indirect
- Single source only
- Some interpretation required

**Example:**
> "Based on the Phase II ESA, groundwater contamination 
> appears to extend northeast [1]."
> Confidence: 68%

**Action:** Review source document, consider additional evidence.

### 🔴 Low Confidence (Below 50%)

**What it means:**
- Limited or weak evidence
- High degree of inference
- May require expert verification

**Example:**
> "The contamination source may be historical industrial use."
> Confidence: 35%

**Action:** Treat as hypothesis, gather more evidence.

## Factors Affecting Confidence

### Evidence Quality
- Document clarity
- Table vs. narrative
- Recent vs. historical

### Query Specificity
- Specific questions → higher confidence
- Broad questions → lower confidence

### Source Authority
- Regulatory documents weighted higher
- Verified documents weighted higher

## Confidence vs. Accuracy

**Important:** Confidence measures evidence support, not truth.

- High confidence = well-supported by documents
- High confidence ≠ absolute truth
- Always verify critical findings

## Improving Confidence

### Upload Better Sources
- Official reports
- Primary data sources
- Verified documents

### Ask Better Questions
- Be specific
- Include context
- Reference known values

### Verify Documents
- Mark documents as verified
- Classify document types
- Update superseded documents

## Using Confidence in Reports

### In Generated Reports
- Confidence shown per section
- Low-confidence items flagged
- Summary statistics available

### Setting Thresholds
Configure minimum confidence:
1. Go to **Project Settings**
2. Set **Minimum Confidence**
3. Low-confidence answers flagged

### Disclosure Language
Low confidence triggers disclosure:
> "Limited evidence available for this finding."
    `,
    keywords: ['confidence', 'score', 'accuracy', 'reliability', 'trust'],
    relatedArticles: ['rag-001', 'rag-003', 'gs-003'],
    lastUpdated: '2025-12-01',
    feature: 'rag',
  },

  // ============================================
  // TABLES & FIGURES
  // ============================================
  {
    id: 'rag-006',
    title: 'Working with Tables & Figures',
    category: 'rag-chat',
    summary: 'Query data from tables and figures',
    content: `
# Working with Tables & Figures

## Table Extraction

### How Tables are Processed
1. **Detection**: AI identifies tables in documents
2. **Structure**: Rows, columns, headers extracted
3. **Context**: Surrounding text captured
4. **Indexing**: Added to Evidence Index

### Table Query Tips

Ask about specific data:
> "What is the benzene concentration at MW-05 in Table 3?"

Compare values:
> "Which wells exceed the arsenic guideline in the 2023 results?"

Summarize tables:
> "List all PFAS compounds detected above 0.07 µg/L"

## Figure Extraction

### Supported Figures
- Charts and graphs
- Maps and diagrams
- Photographs
- Technical drawings

### Figure Processing
1. Image extracted from document
2. OCR applied to text elements
3. Caption and context captured
4. Added to Evidence Index

### Querying Figures

Reference specific figures:
> "What does Figure 5 show about groundwater flow?"

Ask about visual data:
> "Describe the site layout shown in the site plan"

## Evidence Index (Index A)

Tables and figures go to the **Evidence Index**:
- Highest priority in searches
- Specific data retrieval
- Structured information

## Cross-Referencing

### Table to Text
Link table data to narrative:
> "The elevated arsenic levels (Table 4) correspond to 
> the contamination plume described in Section 5.2."

### Figure to Text
Connect figures to descriptions:
> "The groundwater flow direction (Figure 3) indicates 
> off-site migration potential."

## Best Practices

### Document Quality
- Clear, high-resolution tables
- Properly formatted cells
- Consistent headers

### Query Strategy
- Reference table/figure numbers when known
- Ask for specific values
- Use comparison queries

### Verification
- Click citations to view source
- Check original table formatting
- Verify unit conversions
    `,
    keywords: ['table', 'figure', 'chart', 'data', 'extraction'],
    relatedArticles: ['rag-001', 'kb-001', 'kb-003'],
    lastUpdated: '2025-12-01',
    feature: 'rag',
  },

  // ============================================
  // AUDIT TRAIL
  // ============================================
  {
    id: 'adm-006',
    title: 'Audit Trail & Compliance Logging',
    category: 'admin',
    summary: 'Track all activity for compliance',
    content: `
# Audit Trail & Compliance Logging

## Overview

TraceOS maintains comprehensive audit logs for:
- Regulatory compliance
- Security monitoring
- Quality assurance
- Incident investigation

## What's Logged

### User Actions
- Login/logout events
- Document uploads
- Queries performed
- Reports generated
- Data exports
- Settings changes

### System Events
- Processing jobs
- Sync operations
- Errors and failures
- Performance metrics

### Data Access
- Documents viewed
- Entities queried
- Citations accessed
- Data exported

## Audit Log Details

Each log entry includes:
\`\`\`
{
  "timestamp": "2025-01-15T14:30:22Z",
  "user_id": "user-uuid",
  "action": "rag_query",
  "resource_type": "query",
  "details": {
    "question": "What is the arsenic concentration?",
    "project_id": "project-uuid",
    "results_count": 5,
    "confidence": 85
  },
  "ip_address": "192.168.1.100",
  "session_id": "session-uuid"
}
\`\`\`

## Viewing Audit Logs

### Access Logs
1. Go to **Settings** > **Audit Logs**
2. Filter by:
   - Date range
   - User
   - Action type
   - Resource type
3. Search within logs

### Export Logs
1. Select date range
2. Choose format (CSV, JSON)
3. Download

## Compliance Features

### Immutability
- Logs cannot be modified
- Deletion not possible
- Integrity hashes included

### Retention
Default: 7 years (configurable)
- Regulatory requirements vary
- Contact support for custom retention

### Access Control
- Only admins can view logs
- Viewer logs are also logged
- Export requires permissions

## Query Audit (RAG)

### Full Query Logging
Every RAG query records:
- Original question
- Filters applied
- Model used
- Prompts sent
- Chunks retrieved
- Answer generated
- Citations used
- Confidence score

### Compliance Fields
- PII access flagged
- Access denied reasons
- Policy violations

## Using Audit Logs

### Security Review
- Unusual access patterns
- Failed login attempts
- After-hours activity

### Quality Assurance
- Query accuracy review
- Citation verification
- Performance analysis

### Incident Investigation
- Timeline reconstruction
- User activity trace
- Data access history
    `,
    keywords: ['audit', 'log', 'compliance', 'trail', 'security', 'tracking'],
    relatedArticles: ['adm-001', 'adm-005'],
    lastUpdated: '2025-12-01',
    feature: 'admin',
  },

  // ============================================
  // VERSION HISTORY
  // ============================================
  {
    id: 'kb-005',
    title: 'Version History & Document Updates',
    category: 'knowledge-base',
    summary: 'Track document versions and supersession',
    content: `
# Version History & Document Updates

## Document Versions

### Why Version Tracking?
- Regulations change over time
- Documents get updated
- Need to reference correct version
- Audit trail requirements

### Supersession

When you upload a new version:
1. Upload the updated document
2. Select the old document it replaces
3. Click **Mark as Supersession**
4. Old document marked as superseded

### Version Status

| Status | Meaning |
|--------|---------|
| Current | Active, used in searches |
| Superseded | Replaced by newer version |
| Historical | Archived, view-only |

## Finding Versions

### Document Details
1. Click on any document
2. See **Version History** section
3. View all related versions

### Version Timeline
\`\`\`
v3 (Current)    - Jan 2025
    ↑ supersedes
v2 (Superseded) - Jul 2024
    ↑ supersedes
v1 (Historical) - Jan 2024
\`\`\`

## Searching Versions

### Default Behavior
Searches only include current documents.

### Include Historical
Toggle **Include Superseded** in search filters:
- [ ] Current only (default)
- [x] Include superseded versions

### Regulatory Lookup
For regulatory documents, specify version:
> "What was the arsenic guideline in the 2020 NAAQS?"

## Regulatory Documents

### Marking Regulatory Docs
When uploading regulatory documents:
1. Set **Document Type** to "Regulatory"
2. Enter **Regulation Name**
3. Enter **Version/Amendment**
4. Set **Effective Date**
5. Enter **Issuing Authority**

### Automatic Updates
Subscribe to regulation updates:
1. Go to **Settings** > **Regulatory**
2. Select regulations to track
3. Get notified of updates

## Best Practices

### Upload Strategy
- Use consistent naming
- Include version in filename
- Set supersession immediately
- Verify regulatory details

### Naming Convention
\`\`\`
{Document Name}_{Version}_{Date}.pdf

Example:
EIA_Notification_2006_Amended_2022.pdf
Site_Assessment_Phase2_v3_20250115.pdf
\`\`\`

### Regular Review
- Quarterly regulatory review
- Mark outdated documents
- Update supersession chains
    `,
    keywords: ['version', 'history', 'update', 'supersede', 'regulatory'],
    relatedArticles: ['kb-001', 'kb-002'],
    lastUpdated: '2025-12-01',
    feature: 'sources',
  },
];

/**
 * Get articles filtered by category
 */
export function getArticlesByCategory(
  category: HelpCategory,
  articles: HelpArticleData[] = defaultHelpContent
): HelpArticleData[] {
  return articles.filter(article => article.category === category);
}

/**
 * Search articles by keyword
 */
export function searchArticles(
  query: string,
  articles: HelpArticleData[] = defaultHelpContent
): HelpArticleData[] {
  const lowerQuery = query.toLowerCase();
  
  return articles.filter(article => {
    // Search in title
    if (article.title.toLowerCase().includes(lowerQuery)) return true;
    
    // Search in summary
    if (article.summary.toLowerCase().includes(lowerQuery)) return true;
    
    // Search in keywords
    if (article.keywords.some(kw => kw.toLowerCase().includes(lowerQuery))) return true;
    
    // Search in content (partial)
    if (article.content.toLowerCase().includes(lowerQuery)) return true;
    
    return false;
  });
}

/**
 * Get article by ID
 */
export function getArticleById(
  id: string,
  articles: HelpArticleData[] = defaultHelpContent
): HelpArticleData | undefined {
  // Case-insensitive search for article ID
  const found = articles.find(article => article.id === id || article.id.toLowerCase() === id.toLowerCase());
  
  if (process.env.NODE_ENV === 'development' && !found && articles.length > 0) {
    // Debug: log first few article IDs for troubleshooting
    const sampleIds = articles.slice(0, 5).map(a => a.id);
    console.debug(`[getArticleById] Article "${id}" not found. Sample IDs:`, sampleIds);
  }
  
  return found;
}

/**
 * Get related articles
 */
export function getRelatedArticles(
  articleId: string,
  articles: HelpArticleData[] = defaultHelpContent
): HelpArticleData[] {
  const article = getArticleById(articleId, articles);
  if (!article || !article.relatedArticles) return [];
  
  return article.relatedArticles
    .map(id => getArticleById(id, articles))
    .filter((a): a is HelpArticleData => a !== undefined);
}

